import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useGetWidth } from 'utils/useGetWidth'
import { AppFormHook } from 'components/AppFormController'
import {
  useCreateBuffetDishMutation,
  useGetBuffetDishDetailQuery,
  useUpdateBuffetDishMutation,
} from 'core/api/BuffetDishApi'
import { ICategory } from 'core/api/dto/CategoriesObjDto'
import { ICreateBuffetDishDto, IEdgeServers } from 'core/api/dto/BuffetDishDto'
import { IBuffetMilletDto } from 'core/api/dto/BuffetMilletDto'
import { ITag } from 'core/api/dto/TagsDto'
import {
  AppNotification,
  NotificationType,
} from 'components/notification/Notification'
import { AllRoutes } from 'core/routes/AllRoutes'
import { AppInput, InputMaskType } from 'components/AppInput/AppInput'
import AppDropdownWithPagination from 'components/AppDropdown/AppDropdownWithPagination'
import AppMultiDropdownWithPhotoAndPagination from 'components/AppDropdown/AppMultiDropdownWithPhotoAndPagination'
import { useGetCategoryQuery } from 'core/api/CategoryApi'
import { DragAndDropUploadPhoto } from 'pages/BuffetLinePage/components/DragAndDropUploadPhoto'
import { AppMultiDropdownWithPagination } from 'components/AppDropdown/AppMultiDropdownWithPagination'
import { useGetBuffetMilletQuery } from 'core/api/BuffetMilletApi'
import { useGetTagsListQuery } from 'core/api/TagsApi'
import AppBtn, {
  AppBtnColor,
  AppBtnSize,
  IconPosition,
} from 'components/btn/AppBtn'
import { setCurrentData } from 'utils/setCurrentData'
import { InternalDishCategory } from 'core/api/dto/BuffetDishDto'
import { convertBase64 } from 'components/input/InputFile'
import { useDeletePhotoBuffetDishMutation } from '../../../core/api/BuffetDishApi'
import { getMin, setFullTime } from 'utils/formatDate'
import { useGetCurrentUserQuery } from 'core/api/UserApi'
import { useCanEditPrice } from '../hooks/useCanEditPrice'

export enum DetailTab {
  'main' = 'main',
  'info' = 'info',
}

const formData = [
  {
    fieldName: 'groupGuid',
  },
  {
    fieldName: 'name',
    required: 'Обязательное поле',
  },
  {
    fieldName: 'category',
    required: 'Обязательное поле',
  },
  {
    fieldName: 'standardWeight',
  },
  {
    fieldName: 'standardTempMin',
  },
  {
    fieldName: 'standardTempMax',
  },
  {
    fieldName: 'standardDeviation',
  },
  {
    fieldName: 'implementationPeriod',
  },
  {
    fieldName: 'price',
  },
]

export const MainTab = () => {
  const navigate = useNavigate()
  const { dishId } = useParams()
  const { t } = useTranslation()
  const {
    formState,
    formStateChangeHandler,
    formErrorState,
    formValidationHandler,
  } = AppFormHook({
    formConfig: formData,
  })
  const { data: currentDish } = useGetBuffetDishDetailQuery(
    { id: Number(dishId!) },
    { skip: !dishId },
  )
  const { data: currentUser } = useGetCurrentUserQuery()
  const canEditPrice = useCanEditPrice()

  const isDisabled: boolean =
    currentUser?.userRoles.restaurant.source.sourceType === 'EXT_INTEGRATION'

  //Чтобы TS не ругался на несоответствие категорий
  const mapInternalDishCategoryToICategory = (
    internalCategory: InternalDishCategory | undefined,
  ): ICategory | null => {
    if (!internalCategory) return null

    return {
      ...internalCategory,
      restaurant: 0,
      isArchived: internalCategory.is_archived,
    }
  }

  const [currentCategory, setCurrentCategory] = useState<null | ICategory>(null)
  const [milletValue, setMilletValue] = useState<IBuffetMilletDto[]>([])
  const [tagsValue, setTagsValue] = useState<ITag[]>([])
  const [file, setFile] = useState<null | File>(null)
  const [isDeletedFile, setIsDeletedFile] = useState<boolean>(false)
  const [filePreview, setFilePreview] = useState<null | string>(null)
  const [isDirty, setIsDirty] = useState(false)
  const { width } = useGetWidth()

  const [deletePhotoBuffetDish] = useDeletePhotoBuffetDishMutation()

  useEffect(() => {
    if (!!file) {
      setFilePreview(URL.createObjectURL(file))
    }
  }, [file])

  useEffect(() => {
    if (!!currentDish) {
      const currentData = setCurrentData({ data: currentDish, formState })
      formStateChangeHandler({
        ...currentData,
        implementationPeriod: setFullTime(
          currentDish?.implementationPeriodRepresent || '',
        ),
        category: `${currentDish?.internalDishCategory?.id || ''}`,
        price: currentDish?.currentCost ? String(currentDish.currentCost) : '',
      })

      setCurrentCategory(
        mapInternalDishCategoryToICategory(currentDish?.internalDishCategory),
      )

      setFilePreview(
        !!currentDish.imageOnS3 || !!currentDish.image
          ? currentDish.imageOnS3 || currentDish.image
          : null,
      )
      setMilletValue(currentDish.millets)
      setTagsValue(currentDish.dishTags)
      // if (currentDish?.edgeServers && currentDish?.edgeServers?.length) {
      //   setCurrentEdgeServers(currentDish.edgeServers)
      // }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDish])

  const [createBuffetDish, { isLoading: isLoadingCreateDish }] =
    useCreateBuffetDishMutation()
  const [updateBuffetDish, { isLoading: isLoadingUpdateDish }] =
    useUpdateBuffetDishMutation()

  const changeHandler = (propName: string, value: string) => {
    setIsDirty(true)
    formStateChangeHandler({ [propName]: value })
  }

  const changeCategoryHandler = (value: null | ICategory) => {
    setIsDirty(true)
    formStateChangeHandler({ category: `${value?.id}` })
    setCurrentCategory(value)
  }
  const changeMilletHandler = (value: IBuffetMilletDto[]) => {
    setMilletValue(value)
    setIsDirty(true)
  }
  const changeTagHandler = (value: ITag[]) => {
    setTagsValue(value)
    setIsDirty(true)
  }

  const createDishes = async () => {
    const { isValid } = formValidationHandler()
    if (!isValid) return

    const dishData = {
      groupGuid: formState.groupGuid,
      name: formState.name,
      internal_dish_category_id: Number(formState.category),
      implementationPeriod: getMin(formState.implementationPeriod) || null,
      standardWeight: formState.standardWeight
        ? Number(formState.standardWeight)
        : null,
      standardDeviation: formState.standardDeviation
        ? Number(formState.standardDeviation)
        : null,
      standardTempMin: formState.standardTempMin
        ? Number(formState.standardTempMin)
        : null,
      standardTempMax: formState.standardTempMax
        ? Number(formState.standardTempMax)
        : null,
      currentCost: formState.price ? Number(formState.price) : null,
      millets: milletValue.map(({ id }) => id),
      dishTags: tagsValue.map(({ id }) => id),
      image: formState.image as unknown,
    }

    //  передаём  File
    if (file) {
      const base64Image = await convertBase64(file)
      dishData.image = base64Image
    } else if (isDeletedFile) {
      dishData.image = null
    }

    if (isDeletedFile && dishId) {
      deletePhotoBuffetDish({ dish_id: Number(dishId) })
        .unwrap()
        .catch(() => {
          AppNotification({
            msg: t('Ошибка при удалении фото'),
            type: NotificationType.error,
          })
        })
    }

    const handleSuccess = (res: any) => {
      AppNotification({
        msg: t(dishId ? 'Блюдо успешно изменено' : 'Блюдо успешно добавлено'),
        type: NotificationType.success,
      })
      setIsDirty(false)
      navigate(`/${AllRoutes.dishes.path}/`)
    }

    if (dishId) {
      updateBuffetDish({
        id: Number(dishId),
        data: dishData as unknown as ICreateBuffetDishDto,
      })
        .unwrap()
        .then(handleSuccess)
    } else {
      createBuffetDish(dishData as ICreateBuffetDishDto)
        .unwrap()
        .then(handleSuccess)
    }

  }

  return (
    <>
      <div className="tab-content">
        <div className="dish-create-wrap">
          {currentDish && (
            <div className="head-wrap">
              <div className="uuid">
                <b>GUID </b>
                <span className={'color-Gray-medium'}> {currentDish.guid}</span>
              </div>
              {currentDish?.source?.name && (
                <div className="uuid">
                  <b>{t('Источник данных')}</b>
                  <span className={'color-Gray-medium'}>
                    {' '}
                    {currentDish?.source?.name}
                  </span>
                </div>
              )}
            </div>
          )}
          <div className="row">
            <div className="create-col">
              <AppInput
                value={formState.groupGuid}
                onChange={(value) => changeHandler('groupGuid', value)}
                label="ID источника"
                placeholder="ID источника"
                border
                fullWidth
                clear
                id="groupGuid"
                disabled={dishId ? true : false}
                maxLength={100}
              />
              <AppInput
                value={formState.name}
                onChange={(value) => changeHandler('name', value)}
                error={formErrorState.name ? 'Обязательное поле' : undefined}
                label="Наименование"
                placeholder="Название"
                border
                required
                fullWidth
                clear
                id="name"
                disabled={isDisabled}
              />
              {width <= 1200 && (
                <AppDropdownWithPagination
                  label="Категория"
                  placeholder="Выбрать категорию"
                  value={currentCategory}
                  onChange={changeCategoryHandler}
                  getterData={useGetCategoryQuery}
                  propToShowInInput={'name'}
                  propToShowInList={'name'}
                  error={
                    formErrorState.category ? 'Обязательное поле' : undefined
                  }
                  border
                  fullWidth
                  required
                  id="category"
                  disabled={isDisabled}
                  showArchivedItems={false}
                />
              )}
              <AppInput
                value={formState.standardWeight}
                onChange={(value) => changeHandler('standardWeight', value)}
                label="Вес эталонный, кг"
                placeholder="0.000"
                maxLength={6}
                inputMask={InputMaskType.float}
                border
                fullWidth
                clear
                id="standardWeight"
                // disabled={isDisabled}
              />
              {width <= 1200 && (
                <AppInput
                  value={formState.standardDeviation}
                  onChange={(value) =>
                    changeHandler('standardDeviation', value)
                  }
                  label="Отклонение, кг"
                  border
                  id="standardDeviation"
                  placeholder="±0.000"
                  fullWidth
                  inputMask={InputMaskType.float}
                  maxLength={6}
                  clear
                  // disabled={isDisabled}
                />
              )}
              <label className=" w-100 temp-label">{t('Температура эталонная, °C')}</label>
              <div className="input-row mt-10 ">
                <AppInput
                  value={
                    formState.standardTempMin === '0'
                      ? ''
                      : formState.standardTempMin
                  }
                  onChange={(value) => changeHandler('standardTempMin', value)}
                  border
                  fullWidth
                  placeholder="0"
                  maxLength={3}
                  inputMask={InputMaskType.negativeInteger}
                  clear
                  id="standardTempMin"
                  // disabled={isDisabled}
                />
                <AppInput
                  value={
                    formState.standardTempMax === '0'
                      ? ''
                      : formState.standardTempMax
                  }
                  onChange={(value) => changeHandler('standardTempMax', value)}
                  border
                  fullWidth
                  placeholder="0"
                  maxLength={3}
                  inputMask={InputMaskType.negativeInteger}
                  clear
                  id="standardTempMax"
                  // disabled={isDisabled}
                />
              </div>
              {width > 1200 && (
                <DragAndDropUploadPhoto
                  fileURL={filePreview}
                  onSetFile={setFile}
                  setFilePreview={setFilePreview}
                  setIsDirty={setIsDirty}
                  setIsDeletedFile={setIsDeletedFile}
                  // disabled={isDisabled}
                />
              )}
            </div>
            <div className="create-col">
              {width > 1200 && (
                <AppDropdownWithPagination
                  label="Категория"
                  placeholder="Выбрать категорию"
                  value={currentCategory}
                  onChange={changeCategoryHandler}
                  getterData={useGetCategoryQuery}
                  propToShowInInput={'name'}
                  propToShowInList={'name'}
                  error={
                    formErrorState.category ? 'Обязательное поле' : undefined
                  }
                  border
                  fullWidth
                  required
                  id="category"
                  disabled={isDisabled}
                  showArchivedItems={false}
                />
              )}
              {width > 1200 && (
                <AppInput
                  value={formState.standardDeviation}
                  onChange={(value) =>
                    changeHandler('standardDeviation', value)
                  }
                  label="Отклонение, кг"
                  border
                  id="standardDeviation"
                  placeholder="±0.000"
                  fullWidth
                  inputMask={InputMaskType.float}
                  maxLength={6}
                  clear
                  // disabled={isDisabled}
                />
              )}
              <AppInput
                value={formState.implementationPeriod}
                onChange={(value) =>
                  changeHandler('implementationPeriod', value)
                }
                type="time"
                id="implementationPeriod"
                label="Допустимое время реализации, ч:мин"
                placeholder="00:00"
                border
                fullWidth
                clear
                // disabled={isDisabled}
              />
              <AppMultiDropdownWithPhotoAndPagination<
                IBuffetMilletDto,
                keyof IBuffetMilletDto
              >
                label={'Выбор тары'}
                value={milletValue}
                getterData={useGetBuffetMilletQuery}
                propToShowInInput={'id'}
                propToShowInList={'name'}
                propToShowPhotoS3={'imageOnS3'}
                propToShowPhoto={'image'}
                propToShowWheight={'weight'}
                border
                fullWidth
                id="millets"
                placeholder="Выбрать"
                onChange={changeMilletHandler}
                showArchivedItems={false}
              />

              <AppMultiDropdownWithPagination<ITag, keyof ITag>
                label={'Тэги'}
                value={tagsValue}
                getterData={useGetTagsListQuery}
                propToShowInInput={'name'}
                propToShowInList={'name'}
                id="tags"
                border
                fullWidth
                placeholder="Выбрать"
                onChange={changeTagHandler}
                // disabled={isDisabled}
                showArchivedItems={false}
              />

              {/* {!!tagsValue.length && (
                <div className="app-input-wrap">
                  <label>{t('Выбранные теги')}</label>
                  <div className="choose-items-block full-width">
                    {tagsValue.map((item, index) => (
                      <div
                        className="choose-item"
                        key={item.id}
                        id={`tag-${index}`}
                      >
                        {item.name}
                        <i
                          className="an-ico an-ico-close"
                          onClick={() => onRemoveTagItem(item.id)}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )} */}
              <AppInput
                value={formState.price}
                onChange={(value) => changeHandler('price', value)}
                label="Цена, Р"
                placeholder="0.00"
                maxLength={10}
                inputMask={InputMaskType.floatTwoDecimals}
                border
                fullWidth
                clear
                id="price"
                disabled={!canEditPrice}
              />

              {width <= 1200 && (
                <DragAndDropUploadPhoto
                  fileURL={filePreview}
                  onSetFile={setFile}
                  setFilePreview={setFilePreview}
                  setIsDirty={setIsDirty}
                  setIsDeletedFile={setIsDeletedFile}
                  // disabled={isDisabled}
                />
              )}
            </div>
          </div>
          <div className="buttons-block mt-40">
            <AppBtn
              title="Отменить"
              color={AppBtnColor.outlineViolet}
              sized={AppBtnSize.md}
              iconClassName="close"
              iconPosition={IconPosition.left}
              onClick={() => navigate(-1)}
            />
            <AppBtn
              title="Сохранить"
              color={AppBtnColor.violetWhite}
              sized={AppBtnSize.md}
              iconClassName="save"
              iconPosition={IconPosition.left}
              onClick={createDishes}
              isLoading={isLoadingCreateDish || isLoadingUpdateDish}
              // disabled={isDisabled}
            />
          </div>
        </div>
      </div>
    </>
  )
}
